// Preload Gate - Multi-Signal Tiering für alle Browser
// Robust gegen fehlende APIs (Safari, Firefox, iOS)

/**
 * Bestimmt das Geräte-Tier basierend auf mehreren Signalen
 * @returns {'low' | 'mid' | 'high' | 'veryHigh'}
 */
export function getPreloadTier() {
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const dm = navigator.deviceMemory;                 // optional (Chromium only)
  const cores = navigator.hardwareConcurrency || 2;  // breit unterstützt
  const conn = navigator.connection || {};
  const saveData = !!conn.saveData;
  const et = conn.effectiveType || '4g';             // kann fehlen
  const slowNet = /^(slow-2g|2g|3g)$/.test(et);

  // 1) Harte Stopps zuerst
  if (saveData || slowNet) return 'low';

  // 2) Wenn deviceMemory vorhanden → direkt klassifizieren (Chrome/Edge)
  if (typeof dm === 'number') {
    if (dm >= 8) return 'veryHigh';
    if (dm >= 4) return 'high';
    if (dm >= 2) return 'mid';
    return 'low';
  }

  // 3) Fallback-Heuristik (Safari, Firefox, iOS)
  //    Nutze CPU-Kerne + Display-Größe als Proxy
  // eslint-disable-next-line no-restricted-globals
  const px = (screen.width * screen.height * Math.max(1, window.devicePixelRatio || 1) ** 2) || 0;
  const smallDisplay = px < 1_000_000; // grob: < ~720p@1x

  if (isMobile) {
    // Mobile: konservativ
    if (cores <= 2 || smallDisplay) return 'low';
    // High-End Mobile (iPad Pro, Flagship Android)
    return 'mid';
  } else {
    // Desktop ohne deviceMemory (Safari/Firefox)
    if (cores <= 2 || smallDisplay) return 'mid';
    // Moderne Desktop-Browser
    return 'high';
  }
}

/**
 * Kann aggressiv preloaden? (high oder veryHigh tier)
 */
export function canPreloadAggressively() {
  const tier = getPreloadTier();
  return tier === 'high' || tier === 'veryHigh';
}

/**
 * Kann moderat preloaden? (mid, high, oder veryHigh tier)
 */
export function canPreloadModerately() {
  const tier = getPreloadTier();
  return tier === 'mid' || tier === 'high' || tier === 'veryHigh';
}

/**
 * Budget in Bytes basierend auf Tier
 */
export function getPreloadBudgetBytes() {
  const tier = getPreloadTier();
  const MB = 1024 * 1024;

  switch (tier) {
    case 'veryHigh': return 150 * MB;
    case 'high':     return 80  * MB;
    case 'mid':      return 40  * MB;
    default:         return 20  * MB; // low
  }
}

/**
 * Debug Info für alle Browser
 */
export function getDeviceInfo() {
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const dm = navigator.deviceMemory;
  const cores = navigator.hardwareConcurrency || 2;
  const conn = navigator.connection || {};
  const tier = getPreloadTier();
  const budget = getPreloadBudgetBytes();

  // eslint-disable-next-line no-restricted-globals
  const px = (screen.width * screen.height * Math.max(1, window.devicePixelRatio || 1) ** 2) || 0;

  return {
    tier,
    budget: `${Math.round(budget / 1024 / 1024)}MB`,
    isMobile,
    deviceMemory: dm ? `${dm}GB` : 'unknown (fallback used)',
    cores,
    screenPixels: px.toLocaleString(),
    saveData: !!conn.saveData,
    effectiveType: conn.effectiveType || 'unknown',
    downlink: conn.downlink || 'unknown',
    rtt: conn.rtt || 'unknown',
    canAggressivePreload: canPreloadAggressively(),
    canModeratePreload: canPreloadModerately()
  };
}
